/**
 * Editor-facing slot helpers — browser-safe (dom-env only, no fs/Playwright).
 * The app deep-imports this module; the templates barrel also re-exports it,
 * but browser code must not import the barrel (it pulls in node-only stages).
 *
 * The template editor loads the stored slot-preserving editorHtml, fills every
 * slot with an example value so the canvas never renders empty, and blanks the
 * slots again on save so the persisted markup stays canonical: empty slots,
 * values substituted per product at render time.
 */

import "../html-editor/dom-env";
import { restoreAdRefsInMarkup } from "../html-editor/resolve-ad-refs";
import { defaultTarget, effectiveProduces } from "./classify";
import { readFeedValue, resolveFeedImage } from "./resolve";
import type { ProductRecord, Slot, TemplateManifest } from "./types";

/** A slot value in editor space: text, or an image by URL (never a file path). */
export type EditorSlotValue =
  | { kind: "text"; text: string }
  | { kind: "image"; url: string };

/** `ProductRecord` with URL-backed images — what the editor gets over GraphQL. */
export interface EditorProductRecord
  extends Pick<ProductRecord, "id" | "title" | "description" | "userTags"> {
  images: Array<{ slot: string; url: string; primary: boolean }>;
}

/**
 * Pick the example value a slot shows while editing, tiered:
 *   1. feed slot + sample product → the product's real value
 *   2. agent-authored `placeholder` → its text / a sized checker image
 *   3. generic fabrication (`⟨id⟩` text, checker image) — mirrors
 *      `buildAdSoFar`'s conventions.
 */
export function exampleSlotValue(
  slot: Slot,
  product: EditorProductRecord | null,
): EditorSlotValue {
  if (slot.source.from === "feed" && product) {
    const image = resolveFeedImage(product, slot.source.binding);
    if (image) return { kind: "image", url: image.url };
    const text = readFeedValue(product, slot.source.binding);
    if (text !== null) return { kind: "text", text };
  }

  const placeholder = slot.placeholder;
  if (placeholder?.kind === "text") {
    return { kind: "text", text: placeholder.text };
  }
  if (placeholder?.kind === "image") {
    return {
      kind: "image",
      url: checkerImageDataUri(placeholder.width, placeholder.height),
    };
  }

  // Generic fabrication — image-ness is the slot's effective produced kind
  // under the pure default target (no DOM here, so a null tag name; most image
  // slots sit on an <img> and omit the target, leaving the binding/produces
  // kind as the deciding signal).
  const isImage =
    effectiveProduces(slot, defaultTarget(slot, null)) === "image";
  if (isImage) {
    return { kind: "image", url: checkerImageDataUri(240, 240) };
  }
  return { kind: "text", text: `⟨${slot.id}⟩` };
}

/**
 * Substitute editor values into the markup's slots. The counterpart of
 * `substitute()` for editor space: images land as URLs (no `ad:` inlining —
 * the editor resolves refs separately) and a missing value leaves the slot
 * untouched rather than erroring.
 */
export function fillSlots(
  markup: string,
  manifest: TemplateManifest,
  values: ReadonlyMap<string, EditorSlotValue>,
): string {
  return withSlotElements(markup, manifest, ({ el, slot, target }) => {
    const value = values.get(slot.id);
    if (!value) return;
    if (!el) return;
    if (target.kind === "content" && value.kind === "text") {
      el.textContent = value.text;
    } else if (target.kind === "attr" && value.kind === "image") {
      el.setAttribute("src", value.url);
    }
  });
}

/**
 * Reset every slot to its canonical empty form: content slots lose their text,
 * src slots their `src`. Run on the serialized editor doc before saving, so
 * stored template markup never carries the editing session's example values.
 */
export function blankSlots(markup: string, manifest: TemplateManifest): string {
  return withSlotElements(markup, manifest, ({ el, target }) => {
    if (!el) return;
    if (target.kind === "content") {
      el.textContent = "";
    } else {
      el.removeAttribute("src");
    }
  });
}

/** Capture edited samples before clearing slots for storage. */
export function prepareTemplateForSave(
  markup: string,
  manifest: TemplateManifest,
): { html: string; manifest: TemplateManifest } {
  const samples = new Map<string, string>();
  const html = withSlotElements(markup, manifest, ({ el, slot, target }) => {
    if (!el || target.kind !== "content") return;
    if (effectiveProduces(slot, target) !== "text") return;
    let text = el.textContent ?? "";
    // Spaces joining colored phrases belong to the layout, so replacing either slot keeps them.
    if (
      el.tagName.toLowerCase() === "span" &&
      el.parentElement?.getAttribute("data-layer-type") === "text"
    ) {
      for (const side of ["before", "after"] as const) {
        const neighbor =
          side === "before" ? el.previousElementSibling : el.nextElementSibling;
        if (
          neighbor?.tagName.toLowerCase() !== "span" ||
          !neighbor.hasAttribute("data-slot")
        )
          continue;
        const whitespace = (
          side === "before" ? /^[ \t\u00a0]+/ : /[ \t\u00a0]+$/
        ).exec(text)?.[0];
        if (!whitespace) continue;
        el.insertAdjacentText(
          side === "before" ? "beforebegin" : "afterend",
          whitespace,
        );
        text =
          side === "before"
            ? text.slice(whitespace.length)
            : text.slice(0, -whitespace.length);
      }
    }
    samples.set(slot.id, text);
  });
  return {
    html: blankSlots(restoreAdRefsInMarkup(html), manifest),
    manifest: {
      ...manifest,
      slots: manifest.slots.map((slot) => {
        const text = samples.get(slot.id);
        return text === undefined
          ? slot
          : { ...slot, placeholder: { kind: "text", text } };
      }),
    },
  };
}

/**
 * Drop the `data-slot` marker without removing the element. The layer stays
 * on the canvas as static content; current text / src is kept.
 */
export function unbindSlot(markup: string, slotId: string): string {
  const doc = new DOMParser().parseFromString(
    `<!DOCTYPE html><body>${markup}</body>`,
    "text/html",
  );
  doc.body
    .querySelector(`[data-slot="${cssEscape(slotId)}"]`)
    ?.removeAttribute("data-slot");
  return doc.body.innerHTML;
}

export function unbindRemovedSlots(
  markup: string,
  previous: TemplateManifest,
  next: TemplateManifest,
): string {
  const nextIds = new Set(next.slots.map((slot) => slot.id));
  let result = markup;
  for (const slot of previous.slots) {
    if (!nextIds.has(slot.id)) {
      result = unbindSlot(result, slot.id);
    }
  }
  return result;
}

function withSlotElements(
  markup: string,
  manifest: TemplateManifest,
  visit: (args: {
    el: Element | null;
    slot: Slot;
    target: ReturnType<typeof defaultTarget>;
  }) => void,
): string {
  const doc = new DOMParser().parseFromString(
    `<!DOCTYPE html><body>${markup}</body>`,
    "text/html",
  );
  const body = doc.body;
  for (const slot of manifest.slots) {
    const found = body.querySelector(`[data-slot="${cssEscape(slot.id)}"]`);
    const el = found ? effectiveSlotElement(found) : null;
    const target = defaultTarget(slot, el?.tagName.toLowerCase() ?? null);
    visit({ el, slot, target });
  }
  return body.innerHTML;
}

const HT_CROP_ATTR = "data-ht-crop";

/**
 * The element a slot's value actually lands on. A cropped image layer
 * serializes as a `data-ht-crop` frame `<div>` — which carries the layer's
 * identity attributes, `data-slot` included — wrapping the real `<img>`.
 * Substitution, filling, and blanking must reach that inner img (setting or
 * removing `src` on the frame div is invisible), while the frame stays the
 * slot's layout/measurement box. Everything else resolves to itself.
 */
export function effectiveSlotElement(el: Element): Element {
  if (el.tagName.toLowerCase() === "img") return el;
  if (!el.hasAttribute(HT_CROP_ATTR)) return el;
  const imgs = el.querySelectorAll("img");
  return imgs.length === 1 && imgs[0] ? imgs[0] : el;
}

/** The root ad element: the first body child that isn't style/script/link. */
export function findRootElement(body: HTMLElement): Element | null {
  for (const child of Array.from(body.children)) {
    const tag = child.tagName.toLowerCase();
    if (tag !== "style" && tag !== "script" && tag !== "link") return child;
  }
  return null;
}

/**
 * Read the canvas dimensions from the markup root's inline px style. The root
 * MUST declare explicit `width:<n>px;height:<n>px` (we standardize on inline
 * root dims rather than scanning stylesheet rules). Returns null if absent.
 */
export function parseRootDimensions(
  markup: string,
): { width: number; height: number } | null {
  const doc = new DOMParser().parseFromString(
    `<!DOCTYPE html><body>${markup}</body>`,
    "text/html",
  );
  const root = findRootElement(doc.body);
  return root ? parseElementPxBox(root) : null;
}

/** An element's box from its inline px `width` and `height`, rounded. */
export function parseElementPxBox(
  el: Element,
): { width: number; height: number } | null {
  const style = el.getAttribute("style") ?? "";
  const width = pxDeclaration(style, "width");
  const height = pxDeclaration(style, "height");
  return width !== null && height !== null ? { width, height } : null;
}

function pxDeclaration(style: string, prop: string): number | null {
  const match = new RegExp(
    `(?:^|;)\\s*${prop}\\s*:\\s*(\\d+(?:\\.\\d+)?)\\s*px`,
    "i",
  ).exec(style);
  return match ? Math.round(Number(match[1])) : null;
}

/** Minimal CSS attribute-value escape for the data-slot selector. */
export function cssEscape(value: string): string {
  return value.replace(/["\\]/g, "\\$&");
}

/** Sized checker-pattern placeholder image as raw SVG markup. */
export function checkerSvg(width: number, height: number): string {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><defs><pattern id="c" width="40" height="40" patternUnits="userSpaceOnUse"><rect width="40" height="40" fill="#e8e8e8"/><rect width="20" height="20" fill="#d0d0d0"/><rect x="20" y="20" width="20" height="20" fill="#d0d0d0"/></pattern></defs><rect width="${w}" height="${h}" fill="url(#c)"/><text x="${w / 2}" y="${h / 2 + 8}" text-anchor="middle" font-family="sans-serif" font-size="22" fill="#888">image</text></svg>`;
}

/** Sized checker-pattern placeholder image as a self-contained data URI. */
export function checkerImageDataUri(width: number, height: number): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(checkerSvg(width, height))}`;
}
